import { ReactElement, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import BannerWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/BannerWrapper';
import { IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
// import BmsmChart from '~/ui/components/common/Banner/BmsmChart';
import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import { getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface IProps {
  promotion: IGetPromotionResponse;
}

const HeroSection = ({ promotion }: IProps): ReactElement => {
  const { route } = useRouter();
  const { t } = useTranslation('', { keyPrefix: '' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tHero } = useTranslation('', { keyPrefix: '$local.$heroSection' });
  const { t: tLinks } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$heroSection' });

  const [showBMSMModal, setShowBMSMModal] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const openPopup = () => {
    setShowBMSMModal(true);
  };

  const closePopup = () => {
    setShowBMSMModal(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openPopup();
    }
  };

  let RenderBanners;

  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const promoTag = getCookie('promo-tag');
    const hideForPromoTag =
      promoTag === 'air-france-x-sofitel' || promoTag === 'accorhotels-bienvenue';
    setShouldHideBanner(hideForPromoTag);
  }, []);

  const isPostsPage = route.includes('/posts');

  if (shouldHideBanner) {
    RenderBanners = null;
  } else if (promotion?.promo_is_active && promotion?.promo_type !== 'bmsm') {
    RenderBanners = <BannerWrapper promotion={promotion} />;
  } else if (promotion?.promo_is_active && promotion?.promo_type === 'bmsm') {
    RenderBanners = (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-pressed="false"
          className="bmsm-banner-wrapper"
          onClick={openPopup}
          onKeyDown={handleKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBMSMModal}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closePopup}
        >
          {/* <div className="content">
            <BmsmChart />
          </div> */}
        </Modal>
      </>
    );
  } else {
    RenderBanners = null;
  }

  const shouldRenderBanners = !isPostsPage || isClient;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Product data array for easier iteration
  const products = [
    {
      key: 'lifestyle',
      link: tHero('lifestyle.link'),
      image: '/images/homepage/hp-featured-lifestyle.webp',
      alt: tHero('lifestyle.alt'),
      width: 420,
      height: 457,
      cta: false,
    },
    {
      key: 'roomDiffuser',
      link: tHero('roomDiffuser.link'),
      image: '/images/homepage/hp-room-diffuser.webp',
      alt: tHero('roomDiffuser.alt'),
      width: 420,
      height: 280,
      title: tHero('roomDiffuser.title'),
      description: tHero('roomDiffuser.description'),
      cta: true,
    },
    {
      key: 'roomFragrance',
      link: tHero('roomFragrance.link'),
      image: '/images/homepage/hp-room-fragrance.webp',
      alt: tHero('roomFragrance.alt'),
      width: 420,
      height: 280,
      title: tHero('roomFragrance.title'),
      description: tHero('roomFragrance.description'),
      cta: true,
    },
  ];

  const renderProductCard = (product: (typeof products)[0]) => (
    <Link href={product.link} className={`column ${product.key}-product`}>
      <img
        src={product.image}
        alt={product.alt}
        className="product-image"
        width={product.width}
        height={product.height}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
      />
      <div className="column-copy">
        {product.title && <h2>{product.title}</h2>}
        {product.description && <p>{product.description}</p>}
        {product.cta && <span className="btn-text">{tButton('shopNow')}</span>}
      </div>
    </Link>
  );

  return (
    <section className="base-section hero-section main-hp hp-section">
      {/* Main Hero Banner */}
      <div className="main-hp">
        <Link href={tLinks('mainHero')} id="HP_hero">
          <picture>
            <source
              srcSet="/images/homepage/hp-hero-mob.webp"
              media="(max-width: 620px)"
              width={960}
              height={768}
            />
            <img
              src="/images/homepage/hp-hero.webp"
              alt={tHero('mainHeroAlt')}
              className="hero-image"
              width={1440}
              height={600}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
        </Link>
      </div>
      {shouldRenderBanners && RenderBanners}
      {/* Featured Section */}
      <section className="hp_featured">
        <div className="columns-wrap">
          <div className="columns-intro">
            <h1>{tHero('featuredTitle')}</h1>
            <p>{tHero('featuredSubtitle')}</p>
          </div>

          {isMobile ? (
            <div className="swiper-container">
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={20}
                slidesPerView={1.2}
                navigation
                pagination={{ clickable: true }}
                className="bedding-swiper"
              >
                {products.map(product => (
                  <SwiperSlide key={product.key}>{renderProductCard(product)}</SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className="columns-3col">
              {products.map(product => (
                <div key={product.key}>{renderProductCard(product)}</div>
              ))}
            </div>
          )}
        </div>
      </section>
    </section>
  );
};

export default HeroSection;
